import React, { useContext, useEffect, useMemo, useState } from "react"
import clsx from "clsx"
import { clamp, inverseLerp, lerp } from "@/core/utils/math"
import { useDebounce } from "@/utils/useDebounce"
import rangeStyles from "./Slider.module.css"
import { getFractionalDigits, toFixed } from "@/utils/math"
import { showToast } from "@/store/ui-views-state"
import { useShortcuts } from "@/utils/useShortcuts"
import { resolveWaitBlock } from "@/store/processing/image-processing"
import { useTranslation } from "react-i18next"

export interface SliderValueTransform {
	toRange(val: number): number // e.g. -1~1 -> -100~100
	fromRange(val: number): number // e.g. -100~100 -> -1~1
}

interface SliderProps extends React.HTMLProps<HTMLDivElement> {
	title: string
	value: number
	onUpdateEnded: (value: number, displayedValue: number) => void
	onUpdate?: (value: number, displayedValue: number) => void
	onUpdateCancelled?: () => void
	min?: number
	max?: number
	step?: number
	keyboardStep?: number
	shiftKeyboardStep?: number
	plusMinusStep?: number
	shiftPlusMinusStep?: number
	hoverKeyboardStep?: number
	hoverShiftKeyboardStep?: number
	mid?: number
	background?: string
	transform?: SliderValueTransform
	displayUnit?: string
	defaultValue?: number
	disabled?: boolean
	inFocus?: boolean
	compact?: boolean
	titleAlignment?: "left" | "right" | "none"
	displayMax?: boolean
	displayMaxValue?: number
	focusKey?: string
	panelName?: string
	sectionName?: string
	showTickMarks?: boolean
}

interface SliderFocusContextValue {
	enabled: boolean
	focusedKey: string | null
	setFocusedKey: (key: string | null) => void
}

const SliderFocusContext = React.createContext<SliderFocusContextValue | null>(null)

export const SliderFocusProvider = (props: { enabled: boolean; children: React.ReactNode }) => {
	const [focusedKey, setFocusedKey] = useState<string | null>(null)

	useEffect(() => {
		if (!props.enabled) {
			setFocusedKey(null)
		}
	}, [props.enabled])

	const value = useMemo(
		() => ({ enabled: props.enabled, focusedKey, setFocusedKey }),
		[props.enabled, focusedKey],
	)

	return <SliderFocusContext.Provider value={value}>{props.children}</SliderFocusContext.Provider>
}

export const useSliderFocus = () => {
	return useContext(SliderFocusContext)
}

/**
 * Format slider display name with panel and section context for toast messages.
 *
 * If panelName is not provided, returns title as-is.
 *
 * If panelName is provided, uses sectionName when available, otherwise title,
 * and appends `in ${panelName}` to form the final display name
 * (e.g. "Red Hue in Color Mixer", "Shadows Saturation in Color Grading",
 * "Vignette Amount in Effects").
 *
 * Special case for Detail panel noise reduction:
 *   "Luminance Noise Reduction in Detail", "Color Noise Reduction in Detail"
 */
function formatSliderDisplayName(
	title: string,
	panelName?: string,
	sectionName?: string,
	focusKey?: string,
): string {
	if (!panelName) {
		return title
	}

	// Detail panel noise reduction sliders: use focusKey to identify them
	// instead of comparing translated title strings against English.
	if (focusKey === "luminanceNoiseReduction" || focusKey === "colorNoiseReduction") {
		return `${title} Noise Reduction in ${panelName}`
	}

	const displayName = sectionName ?? title
	return `${displayName} in ${panelName}`
}

export const Slider = (props: SliderProps) => {
	const { t } = useTranslation("components")
	const {
		transform = defaultSliderTransform,
		min = -100,
		max = 100,
		step = 1,
		keyboardStep,
		shiftKeyboardStep,
		plusMinusStep,
		shiftPlusMinusStep,
		mid,
		title,
		value,
		background,
		displayUnit,
		defaultValue,
		disabled,
		onUpdate,
		onUpdateEnded,
		onUpdateCancelled,
		className,
		inFocus,
		compact,
		titleAlignment,
		displayMax,
		displayMaxValue,
		focusKey,
		panelName,
		sectionName,
		showTickMarks,
		...otherProps
	} = props

	const focusContext = useSliderFocus()
	const focusEnabled = Boolean(focusContext?.enabled && focusKey)
	const effectiveInFocus =
		inFocus ?? (focusEnabled ? focusContext?.focusedKey === focusKey : false)
	const effectivePlusMinusStep = plusMinusStep ?? (focusContext?.enabled ? 5 : undefined)
	const effectiveShiftPlusMinusStep =
		shiftPlusMinusStep ?? (focusContext?.enabled ? 20 : undefined)

	const fractionalDigits = getFractionalDigits(step)
	const defaultInputValue = toFixed(transform.toRange(defaultValue ?? 0), fractionalDigits)
	const inputValue = toFixed(transform.toRange(value), fractionalDigits)

	const [val, setVal] = useState(inputValue)
	const [startVal, setStartVal] = useState(0)
	const [isHover, setIsHover] = useState(false)
	const [isChangingHovered, setChangingHovered] = useState(false)
	const [highlightTitle, setHighlightTitle] = useState(false)
	const [highlightTimeout, setHighlightTimeout] = useState<NodeJS.Timeout | null>(null)

	useShortcuts({
		keys: ["up", "shift+up"],
		action: (event) => changeAdjustment(true, "arrow", event.shiftKey, false, true),
		trackingLabel: "increase_hovered_slider",
		trackingValue: () => title,
		disabled: disabled || !isHover,
		preventDefault: true,
	})

	useShortcuts({
		keys: ["down", "shift+down"],
		action: (event) => changeAdjustment(false, "arrow", event.shiftKey, false, true),
		trackingLabel: "decrease_hovered_slider",
		trackingValue: () => title,
		disabled: disabled || !isHover,
		preventDefault: true,
	})
	useShortcuts({
		keys: ["=", "shift!+"],
		action: (event) => changeAdjustment(true, "plusMinus", event.shiftKey),
		trackingLabel: "increase_slider",
		disabled: disabled || !effectiveInFocus,
		preventDefault: true,
		splitKey: "!",
	})

	useShortcuts({
		keys: ["-", "shift+_"],
		action: (event) => changeAdjustment(false, "plusMinus", event.shiftKey),
		trackingLabel: "decrease_slider",
		disabled: disabled || !effectiveInFocus,
		preventDefault: true,
	})

	useEffect(() => {
		setVal(inputValue)
	}, [inputValue])

	// Only show the +/- toast if this slider is focused by user action, not on initial project open
	const [hasInteracted, setHasInteracted] = useState(false)
	useEffect(() => {
		if (effectiveInFocus) {
			if (hasInteracted) {
				displayToastAndHighlightTitle()
			}
		} else {
			setHighlightTitle(false)
		}
	}, [effectiveInFocus, hasInteracted, title, panelName, sectionName])

	// Mark as interacted when user clicks or uses keyboard on the slider
	const handleClick = () => {
		setHasInteracted(true)
		if (focusEnabled && focusKey) {
			focusContext?.setFocusedKey(focusKey)
			const displayName = formatSliderDisplayName(title, panelName, sectionName, focusKey)
			showToast(t("slider.toastModifyWithKeys", { displayName }))
		}
	}

	// Also mark as interacted on keyboard events
	useEffect(() => {
		if (effectiveInFocus && !hasInteracted) {
			const handler = () => setHasInteracted(true)
			window.addEventListener("keydown", handler, { once: true })
			return () => window.removeEventListener("keydown", handler)
		}
	}, [effectiveInFocus, hasInteracted])

	const displayToastAndHighlightTitle = (value?: number, increase?: boolean) => {
		if (value || value === 0) {
			const displayValue = Number.isInteger(value) ? value : parseFloat(value.toFixed(2))
			const displayName = formatSliderDisplayName(title, panelName, sectionName, focusKey)
			showToast(
				increase
					? t("slider.toastIncrease", { displayName, displayValue })
					: t("slider.toastDecrease", { displayName, displayValue }),
			)
		}

		highlightTimeout && clearTimeout(highlightTimeout)
		setHighlightTitle(true)
		setHighlightTimeout(
			setTimeout(() => {
				setHighlightTitle(false)
			}, 3000),
		)
	}

	/**
	 * @param increase true for up, false for down
	 * @param mode "arrow" for slider hover, "plusMinus" for +/- keys
	 * @param isShiftPressed whether Shift was pressed for this shortcut event
	 * @param displayToast whether to show toast
	 * @param isHoverMode true if triggered by slider hover, false if not specified
	 */
	const changeAdjustment = (
		increase: boolean,
		mode: "arrow" | "plusMinus",
		isShiftPressed: boolean,
		displayToast: boolean = true,
		isHoverMode?: boolean,
	) => {
		if (disabled) {
			return
		}

		let effectiveStep: number

		if (mode === "plusMinus") {
			if (isShiftPressed) {
				effectiveStep =
					effectiveShiftPlusMinusStep ?? effectivePlusMinusStep ?? keyboardStep ?? step
			} else {
				effectiveStep = effectivePlusMinusStep ?? keyboardStep ?? step
			}
		} else {
			if (isHoverMode) {
				// Use hover-specific steps if provided
				if (isShiftPressed) {
					effectiveStep =
						props.hoverShiftKeyboardStep ??
						props.hoverKeyboardStep ??
						shiftKeyboardStep ??
						keyboardStep ??
						step * 10
				} else {
					effectiveStep = props.hoverKeyboardStep ?? keyboardStep ?? step
				}
			} else {
				// Fallback to normal keyboard steps
				if (isShiftPressed) {
					effectiveStep = shiftKeyboardStep ?? (keyboardStep ?? step) * 10
				} else {
					effectiveStep = keyboardStep ?? step
				}
			}
		}

		const newValue = val + effectiveStep * (increase ? 1 : -1)
		const boundedValue = clamp(toFixed(newValue, fractionalDigits), min, max)

		if (isChangingHovered == false) {
			setChangingHovered(true)
		}

		update(boundedValue)
		scheduledCommit(boundedValue)

		if (displayToast) {
			displayToastAndHighlightTitle(boundedValue, increase)
		}
	}

	const reset = () => {
		if (val !== defaultInputValue) {
			update(defaultInputValue)
			onUpdateEnded(transform.fromRange(defaultInputValue), defaultInputValue)
		}
	}

	const start = (newVal: number) => {
		newVal = toFixed(newVal, fractionalDigits)
		newVal = clamp(newVal, min, max)

		setStartVal(newVal)
	}

	const update = (newVal: number) => {
		newVal = toFixed(newVal, fractionalDigits)
		newVal = clamp(newVal, min, max)

		setVal(newVal)

		onUpdate?.(transform.fromRange(newVal), newVal)
	}

	const scheduledCommit = useDebounce((newVal: number) => {
		setChangingHovered(false)
		commit(newVal)
	}, 400)

	const commit = (newVal: number) => {
		newVal = toFixed(newVal, fractionalDigits)
		newVal = clamp(newVal, min, max)

		setVal(newVal)

		if (startVal === newVal) {
			resolveWaitBlock("adjustments")
			onUpdateCancelled?.()
			return
		}

		onUpdateEnded(transform.fromRange(newVal), newVal)
	}

	const handlePointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
		setIsHover(true)
		const activeElement = document.activeElement
		if (activeElement instanceof HTMLElement && e.currentTarget.contains(activeElement)) {
			activeElement.blur()
		}
		props.onPointerEnter?.(e)
	}

	const handlePointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
		setIsHover(false)
		props.onPointerLeave?.(e)
	}

	return (
		<div
			{...otherProps}
			className={clsx(
				className,
				isHover && "!opacity-100",
				highlightTitle && "!opacity-100",
				"text-base-50 opacity-50 transition-opacity",
				disabled && "pointer-events-none !opacity-25",
			)}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
		>
			<div
				role="group"
				aria-label={title.toWellFormed() + " Adjustment"}
				className="my-0.5 flex items-center justify-between text-xs"
			>
				{titleAlignment !== "none" && (
					<div
						aria-hidden
						className={clsx(
							"mr-2 flex shrink-0 flex-row items-center capitalize",
							titleAlignment === "left" ? "justify-start" : "justify-end",
							{
								"w-[60px]": !compact,
								"w-[45px]": compact,
							},
						)}
						onDoubleClick={reset}
						onClick={handleClick}
					>
						{title}
					</div>
				)}

				<RangeInput
					className="mr-1 flex-1"
					tabIndex={-1} // let tab focus on slider and keyboard input separately
					onDoubleClick={reset}
					background={background}
					value={val}
					mid={mid}
					min={min}
					max={max}
					step={step}
					disabled={disabled}
					showTickMarks={showTickMarks}
					onUpdateStarted={start}
					onUpdate={update}
					onUpdateEnded={commit}
					aria-label={title}
					aria-valuetext={`${val}${displayUnit ?? ""}`}
				/>

				<TextInput
					className={clsx(
						!disabled && val !== defaultInputValue ? "text-base-50" : "text-base-400",
						!disabled && isHover && "bg-base-50/10",
						"flex w-[6ch] flex-row items-center",
					)}
					value={val}
					step={step}
					keyboardStep={keyboardStep}
					shiftKeyboardStep={shiftKeyboardStep}
					displayUnit={displayUnit}
					sliderTitle={title}
					onUpdateStarted={start}
					onUpdate={update}
					onUpdateEnded={commit}
					data-test={`slider-text-${title.toLowerCase().replace(" ", "-")}`}
				/>
				{displayMax && (
					<div className="text-xs text-base-400">
						{t("slider.maxTotal", { max: displayMaxValue ?? max })}
					</div>
				)}
			</div>
		</div>
	)
}

interface TextInputProps extends React.HTMLProps<HTMLDivElement> {
	value: number
	step: number
	keyboardStep?: number
	shiftKeyboardStep?: number
	displayUnit?: string
	sliderTitle?: string
	onUpdateStarted: (val: number) => void
	onUpdate: (val: number) => void
	onUpdateEnded: (val: number) => void
}

const TextInput = ({
	value,
	step,
	keyboardStep,
	shiftKeyboardStep,
	displayUnit,
	sliderTitle,
	onUpdateStarted,
	onUpdate,
	onUpdateEnded,
	...props
}: TextInputProps) => {
	const [tempVal, setTempVal] = useState("")
	const [isInputting, setIsInputting] = useState(false)

	useEffect(() => {
		setTempVal(value.toString())
	}, [value])

	const getInputValue = (input: HTMLInputElement) => {
		const inputVal = Number(input.value)
		return isNaN(inputVal) ? value : inputVal
	}

	const stepValue = (increment: number) => {
		onUpdate(value + increment)
	}

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select() // select whole text
		setTempVal(value.toString())

		setIsInputting(true)

		onUpdateStarted(getInputValue(e.currentTarget))
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		let effectiveStep: number
		if (e.shiftKey) {
			effectiveStep = shiftKeyboardStep ?? (keyboardStep ?? step) * 10
		} else {
			effectiveStep = keyboardStep ?? step
		}
		if (e.key === "ArrowDown") {
			stepValue(-effectiveStep)
		} else if (e.key === "ArrowUp") {
			stepValue(+effectiveStep)
		} else if (e.key === "Enter" || e.key === "Escape") {
			handleInputConfirm(e)
		}
	}

	const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
		setTempVal(e.currentTarget.value)
	}

	const handleInputConfirm = (e: React.FormEvent<HTMLInputElement>) => {
		setIsInputting(false)

		const inputValue = getInputValue(e.currentTarget)

		onUpdate(inputValue)
		onUpdateEnded(inputValue)

		e.currentTarget.blur()
	}

	return (
		<div {...props}>
			<input
				type="text"
				aria-label={sliderTitle ? `${sliderTitle} value` : undefined}
				className="h-5 w-full flex-1 rounded bg-transparent pr-1 text-right ring-base-300 focus:bg-base-700 focus:text-base-50 focus:ring-1"
				onFocus={handleFocus}
				onKeyDown={handleKeyDown}
				onBlur={handleInputConfirm}
				onChange={handleInput}
				value={isInputting ? tempVal : value}
			/>
			<div>{displayUnit ?? ""}</div>
		</div>
	)
}

const RangeInput = ({
	background,
	value,
	onUpdate,
	onUpdateStarted,
	onUpdateEnded,
	mid,
	min,
	max,
	step,
	className,
	showTickMarks,
	...props
}: React.HTMLProps<HTMLInputElement> & {
	value: number
	background?: string
	mid?: number
	min: number
	max: number
	step?: number
	showTickMarks?: boolean
	onUpdateStarted: (val: number) => void
	onUpdate: (val: number) => void
	onUpdateEnded: (val: number) => void
}) => {
	const ref = React.useRef<HTMLInputElement>(null)

	const fromSliderValue = (val: number) => {
		if (mid) {
			return remapMidPoint(val, mid, min, max)
		}

		return val
	}

	const toSliderValue = (val: number) => {
		if (mid) {
			return remapMidPoint(val, mid, min, max, true)
		}

		return val
	}

	const getSliderValue = (slider: HTMLInputElement) => {
		const inputVal = Number(slider.value)
		const val = isNaN(inputVal) ? value : inputVal
		return fromSliderValue(val)
	}

	const handleSliderPointerDown = (e: React.PointerEvent<HTMLInputElement>) => {
		onUpdateStarted(getSliderValue(e.currentTarget))
		document.addEventListener(
			"pointerup",
			() => {
				ref.current?.blur()
			},
			{ once: true },
		)
	}

	const handleSliderChange = (e: React.FormEvent<HTMLInputElement>) => {
		onUpdate(getSliderValue(e.currentTarget))
	}

	const handleSliderPointerUp = (e: React.PointerEvent<HTMLInputElement>) => {
		e.currentTarget.blur()
	}

	const handleSliderBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		onUpdateEnded(getSliderValue(e.currentTarget))
	}

	//TODO: this is causing issues with slider change being saved more than once
	// const handleSliderPointerLeave = (e: React.PointerEvent<HTMLInputElement>) => {
	// 	if (isChanging) {
	// 		setIsChanging(false)
	// 		onUpdateEnded(getSliderValue(e.currentTarget))
	// 		e.currentTarget.blur()
	// 	}
	// }

	const sliderStyle = React.useMemo(() => {
		const style: React.CSSProperties = {}
		if (background) {
			style["--slider-track-bg"] = background
		}
		return style
	}, [background])

	const input = (
		<input
			ref={ref}
			className={clsx(
				rangeStyles.rangeSlider,
				showTickMarks && rangeStyles.rangeSliderWithTicks,
				showTickMarks ? "relative z-10 w-full" : className,
			)}
			style={sliderStyle}
			type="range"
			min={min}
			max={max}
			step={step}
			value={toSliderValue(value)}
			onInput={handleSliderChange}
			onPointerDown={handleSliderPointerDown}
			onPointerUp={handleSliderPointerUp}
			onBlur={handleSliderBlur}
			{...props}
		/>
	)

	if (!showTickMarks) {
		return input
	}

	const tickCount = Math.floor((max - min) / (step ?? 1)) + 1

	return (
		<div className={clsx("relative", className)}>
			<div className="pointer-events-none absolute inset-x-0 top-0 h-6">
				<div className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-base-500" />
				<div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between">
					{Array.from({ length: tickCount }, (_, index) => {
						const tickValue = min + index * (step ?? 1)
						return (
							<div
								key={index}
								className={clsx(
									"size-1 rounded-full bg-base-400",
									tickValue === value && "opacity-0",
								)}
							/>
						)
					})}
				</div>
			</div>
			{input}
		</div>
	)
}

const defaultSliderTransform: SliderValueTransform = {
	toRange: (val) => Math.round(val * 100),
	fromRange: (val) => val / 100,
}

export const noopSliderTransform: SliderValueTransform = {
	toRange: (val) => val,
	fromRange: (val) => val,
}

const remapMidPoint = (val: number, mid: number, min: number, max: number, reverse?: boolean) => {
	const naturalMid = lerp(0.5, min, max)

	if (mid === naturalMid) {
		return val
	}

	const [fromMid, toMid] = reverse ? [mid, naturalMid] : [naturalMid, mid]

	return val < fromMid
		? lerp(inverseLerp(val, min, fromMid), min, toMid)
		: lerp(inverseLerp(val, fromMid, max), toMid, max)
}

export const sliderBackgrounds = {
	temperature: `linear-gradient(90deg, #0060ff, #ffb100)`,
	tint: `linear-gradient(90deg, #00ff15, #c629ff)`,
	contrast: `linear-gradient(90deg, #5b5b5b, rgba(128, 128, 128, 0.5)), linear-gradient(90deg, black, white, black, white, black, white, black, white, black, white, black, white, black, white, black)`,
	saturation: `linear-gradient(90deg, #5b5b5b, transparent), linear-gradient(90deg, #815bd8, #815bd8, #5471d1, #54cea7, #5fd25a, #cabf3f, #e2513d)`,
	calibrationRedHue: `linear-gradient(90deg, hsl(-45deg, 100%, 50%), hsl(45deg, 100%, 50%))`,
	calibrationRedSat: `linear-gradient(90deg, hsl(0deg, 0%, 50%), hsl(0deg, 100%, 50%))`,
	calibrationGreenHue: `linear-gradient(90deg, hsl(75deg, 100%, 50%), hsl(165deg, 100%, 50%))`,
	calibrationGreenSat: `linear-gradient(90deg, hsl(120deg, 0%, 50%), hsl(120deg, 100%, 50%))`,
	calibrationBlueHue: `linear-gradient(90deg, hsl(195deg, 100%, 50%), hsl(285deg, 100%, 50%))`,
	calibrationBlueSat: `linear-gradient(90deg, hsl(240deg, 0%, 50%), hsl(240deg, 100%, 66%))`,
	defringePurpleAmount: `linear-gradient(90deg, #81308F, #000000)`,
	defringePurpleHue: `linear-gradient(90deg, #4BA770, #81308F, #A47070)`,
	defringeGreenAmount: `linear-gradient(90deg, #5F943F, #000000)`,
	defringeGreenHue: `linear-gradient(90deg, #A47070, #5F943F, #4BA770)`,
}
